"use client";
// ENG-SOT-050 first slice — the /contact-us feedback form. Posts same-origin to /api/feedback only.
import { useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { trackContactFormSubmit } from "@/lib/analytics-event";
import { UNCONFIRMED_MESSAGE, outcomeFromResponse } from "@/lib/feedback-outcome";

// Kept in step with FEEDBACK_TOPICS in lib/feedback-intake.ts (a server module; not imported into the client bundle).
const TOPICS = [
  ["general", "General Questions"], ["booking", "Booking Issue"], ["website", "Problem With Website"],
  ["refund", "Refunds / Cancellations"], ["complaint", "Complaint"], ["business", "Business Inquiry"],
  ["feedback", "General Feedback"], ["other", "Other"],
] as const;

const label: CSSProperties = { display: "block", fontSize: 15, fontWeight: 700, marginBottom: 6, color: "#0A1F44" };
const input: CSSProperties = { width: "100%", boxSizing: "border-box", minHeight: 44, padding: "10px 12px", fontSize: 16, border: "1px solid #94A3B8", borderRadius: 8, font: "inherit" };
const errStyle: CSSProperties = { color: "#B91C1C", fontSize: 14, margin: "4px 0 0" };

export default function FeedbackForm() {
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setFailure(null); setFields({});
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    try {
      const res = await fetch("/api/feedback", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      // Success needs a well-formed case id; a 2xx without one is never shown as "received". Input is never cleared.
      const out = outcomeFromResponse(res.ok, await res.json().catch(() => null));
      if (out.kind === "success") {
        // GA4 contact_form_submit for the genuine receipt only; de-duplicated by case id, which is never sent.
        trackContactFormSubmit(out.caseId);
        setReceipt(out.caseId);
        return;
      }
      if (out.fields) setFields(out.fields);
      setFailure(out.message);
    } catch {
      setFailure(UNCONFIRMED_MESSAGE);
    } finally {
      setBusy(false);
    }
  }

  if (receipt) {
    return (
      <div role="status" style={{ border: "1px solid #86EFAC", background: "#F0FDF4", borderRadius: 12, padding: 20 }}>
        <p style={{ margin: 0, fontWeight: 800 }}>Your message was received.</p>
        <p style={{ margin: "8px 0 0" }}>Case reference: <strong>{receipt}</strong>. Keep it if you write to us again.</p>
      </div>
    );
  }

  const field = (name: string, text: string, opts: { type?: string; required?: boolean; autoComplete?: string; max: number }) => (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={`fb-${name}`} style={label}>{text}{opts.required ? " *" : ""}</label>
      <input id={`fb-${name}`} name={name} type={opts.type ?? "text"} required={opts.required} maxLength={opts.max} autoComplete={opts.autoComplete}
        aria-invalid={fields[name] ? true : undefined} aria-describedby={fields[name] ? `fb-${name}-err` : undefined} style={input} />
      {fields[name] && <p id={`fb-${name}-err`} style={errStyle}>{fields[name]}</p>}
    </div>
  );

  return (
    <form data-feedback-form onSubmit={onSubmit} noValidate aria-labelledby="fb-title" style={{ maxWidth: 640 }}>
      <h2 id="fb-title" style={{ fontSize: 26, fontWeight: 800, margin: "0 0 8px" }}>Send us a message</h2>
      <p style={{ margin: "0 0 20px", color: "#334155" }}>Tell us what happened. Your message counts as received only once you see a case reference.</p>
      {failure && <div role="alert" style={{ ...errStyle, fontSize: 15, marginBottom: 16, padding: 12, border: "1px solid #FCA5A5", borderRadius: 8, background: "#FEF2F2" }}>{failure}</div>}
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="fb-topic" style={label}>Topic *</label>
        <select id="fb-topic" name="topic" required defaultValue="" aria-invalid={fields.topic ? true : undefined} aria-describedby={fields.topic ? "fb-topic-err" : undefined} style={input}>
          <option value="" disabled>Choose a topic</option>
          {TOPICS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        {fields.topic && <p id="fb-topic-err" style={errStyle}>{fields.topic}</p>}
      </div>
      {field("name", "Your name", { required: true, autoComplete: "name", max: 100 })}
      {field("email", "Email address", { type: "email", required: true, autoComplete: "email", max: 254 })}
      {field("reference", "Booking reference (optional; not verified)", { max: 60 })}
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="fb-message" style={label}>Message *</label>
        <textarea id="fb-message" name="message" required rows={6} maxLength={2000} aria-invalid={fields.message ? true : undefined}
          aria-describedby={fields.message ? "fb-message-err" : undefined} style={{ ...input, resize: "vertical" }} />
        {fields.message && <p id="fb-message-err" style={errStyle}>{fields.message}</p>}
      </div>
      {/* Honeypot — hidden from people and assistive tech; bots fill it. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label>Leave this empty<input name="website" tabIndex={-1} autoComplete="off" /></label>
      </div>
      <button type="submit" disabled={busy} style={{ minHeight: 48, padding: "0 28px", fontSize: 16, fontWeight: 800, color: "#fff", background: "#0A1F44", border: 0, borderRadius: 999, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
        {busy ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
